import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import DogCard from "../components/DogCard";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search } from "lucide-react";

export default function DogsDirectory() {
    const [dogs, setDogs] = useState([]);
    const [q, setQ] = useState("");
    const [size, setSize] = useState("");
    const [gender, setGender] = useState("");
    const [energy, setEnergy] = useState("");
    const [region, setRegion] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get("/dogs").then((r) => { setDogs(r.data); setLoading(false); });
    }, []);

    const regions = useMemo(() => Array.from(new Set(dogs.map((d) => d.region).filter(Boolean))).sort(), [dogs]);

    const filtered = useMemo(() => dogs.filter((d) => {
        if (q && !`${d.name} ${d.breed} ${d.location}`.toLowerCase().includes(q.toLowerCase())) return false;
        if (size && d.size !== size) return false;
        if (gender && d.gender !== gender) return false;
        if (energy && d.energy_level !== energy) return false;
        if (region && d.region !== region) return false;
        return true;
    }), [dogs, q, size, gender, energy, region]);

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="dogs-directory">
            <div className="mb-8">
                <div className="eyebrow mb-3">The Directory · {dogs.length} dogs across {regions.length} regions</div>
                <h1 className="font-serif text-4xl lg:text-6xl tracking-tight">Every dog, one search.</h1>
                <p className="mt-3 text-[var(--rl-text-2)] max-w-2xl">Browse dogs from partner rescues and shelters across the network — worldwide.</p>
            </div>

            <div className="flex flex-wrap gap-3 mb-8 items-center">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--rl-text-3)]" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, breed, location…"
                           className="pl-9 border-[var(--rl-border)] h-11 rounded-full" data-testid="dogs-search" />
                </div>
                <Select value={size} onValueChange={(v) => setSize(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-36 border-[var(--rl-border)] h-11 rounded-full" data-testid="dogs-size-filter"><SelectValue placeholder="Size" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Any size</SelectItem>
                        <SelectItem value="small">Small</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="large">Large</SelectItem>
                        <SelectItem value="xl">XL</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={gender} onValueChange={(v) => setGender(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-36 border-[var(--rl-border)] h-11 rounded-full" data-testid="dogs-gender-filter"><SelectValue placeholder="Gender" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Any</SelectItem>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={energy} onValueChange={(v) => setEnergy(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-40 border-[var(--rl-border)] h-11 rounded-full" data-testid="dogs-energy-filter"><SelectValue placeholder="Energy" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Any energy</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={region} onValueChange={(v) => setRegion(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-40 border-[var(--rl-border)] h-11 rounded-full" data-testid="dogs-region-filter"><SelectValue placeholder="Region" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All regions</SelectItem>
                        {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                </Select>
                <div className="ml-auto text-xs text-[var(--rl-text-3)]">{filtered.length} results</div>
            </div>

            {loading ? (
                <div className="py-20 text-center text-[var(--rl-text-2)]">Loading dogs…</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filtered.map((d) => <DogCard key={d.id} dog={d} urgent={d.is_at_risk} />)}
                </div>
            )}
        </div>
    );
}
